import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import eyeIcon from "../../../assets/icons/eyeicon.svg";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  transactionFieldLabels,
  transactionSearchTermDefaultValues,
  transactionsExcelFormFields,
  transactionStateOptions,
  transactionTerminalOptions,
} from "../../../lib/constants";
import {
  useGetTransactionList,
  useUpdateTransactionVerified,
} from "../../../lib/queries/queriesAndMutations";
import { fomatCardNumber, unixToPersianDay, unixToPersianTime } from "../../../utils/utils";
import CopyButton from "../../shared/CopyButton";
import Loader from "../../shared/Loader";
import Pagination from "../../shared/Pagination";
import SettlmentTooltipBtn from "../../shared/SettlmentTooltipBtn";
import TableLoader from "../../shared/TableLoader";
import ValueFinder from "../../shared/ValueFinder";
import VerfiedTooltipBtn from "../../shared/VerfiedTooltipBtn";
import AlertButton from "../../ui/AlertButton";
import HeaderSelect from "../../ui/HeaderSelect";
import AdvanceSearchBtn from "./PopupBtns/AdvanceSearchBtn";
import ExcelPopupBtn from "./PopupBtns/ExcelPopupBtn";
import FilterDetailBar from "./PopupBtns/FilterDetailBar";
import TrasnactionCard from "./TrasnactionCard";

const NewTransactionsList = () => {
  const navigate = useNavigate();

  const [transactionType, setTransactionType] = useState({
    title: "همه",
    value: "",
    active: true,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const [searchTerm, setSearchTerm] = useState({
    ...transactionSearchTermDefaultValues,
    transaction_type: transactionType.value,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 100);

  const type = useSelector((store) => store.bussinessSlice.type);
  const businessId = useSelector((store) => store.bussinessSlice?.data?.business?.id);

  const onPageChange = useCallback((page) => setCurrentPage(page), []);

  const {
    data: transactionList,
    isPending,
    refetch,
    isRefetching,
    isError,
  } = useGetTransactionList({
    type,
    businessId,
    searchTerm: debouncedSearchTerm,
    currentPage,
    pageSize,
    transactionType: transactionType.value,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Memoize the options array for HeaderSelect
  const transactionTypeOptions = useMemo(
    () => [
      { title: "همه", value: "", active: true },
      { title: "فرم", value: "form", active: false },
      { title: "درگاه", value: "gateway", active: false },
    ],
    [],
  );

  useEffect(() => {
    refetch();
  }, [transactionType, debouncedSearchTerm, currentPage, refetch]);

  if (isPending) {
    return (
      <div className="w-full h-full flex-center min-h-[400px] ">
        <Loader />
      </div>
    );
  }
  if (isRefetching) {
    return (
      <div className="w-full h-full flex-center min-h-[400px] ">
        <Loader />
      </div>
    );
  }
  if (isError) {
    return <div className="w-full h-full flex-center min-h-[400px] ">خطا در سرور</div>;
  }

  return (
    <div className="py-4 px-1">
      <div className="  rounded-v-lg p-2 relative flex flex-col overflow-hidden  w-full pb-10 bg-white h-full">
        <div className="flex gap-2 justify-between w-full ">
          {/* Use memoized options and callback for HeaderSelect */}
          <HeaderSelect
            activeItem={transactionType}
            setActiveItem={setTransactionType}
            options={transactionTypeOptions}
          />
          <div className="flex gap-2">
            <ExcelPopupBtn
              searchTermRef={debouncedSearchTerm}
              fields={transactionsExcelFormFields}
              url={"transactions"}
              type={type}
              businessId={businessId}
            />
            <AdvanceSearchBtn setSearchTerm={setSearchTerm} />
          </div>
        </div>

        <FilterDetailBar
          searchTerm={debouncedSearchTerm}
          setSearchTerm={setSearchTerm}
          labelFields={transactionFieldLabels}
        />

        <div className=" overflow-scroll no-scrollbar hidden md:block min-h-[67vh]">
          <table className="w-full" id="albums" cellSpacing="1">
            <thead>
              <tr>
                <th scope="col">نوع پذیرنده</th>
                <th scope="col">درگاه/فرم/کارتخوان</th>
                <th scope="col">شماره پیگیری وندا</th>
                <th scope="col">مبلغ (ریال)</th>
                <th scope="col">تاریخ پرداخت</th>
                <th scope="col">وضعیت تراکنش</th>
                <th scope="col">شماره سفارش کاربر</th>
                <th scope="col">شماره کارت</th>
                <th scope="col">جزییات</th>
              </tr>
            </thead>

            <tbody>
              {isRefetching ? (
                <TableLoader />
              ) : transactionList.count > 0 ? (
                transactionList.items.map((item, index) => (
                  <TransactionRow key={index} item={item} navigate={navigate} refetch={refetch} />
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-start py-4">
                    آیتمی وجود ندارد
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="w-full bg-neutral-20 p-2 grid grid-cols-1  lg:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4 gap-4 md:p-2 rounded-[16px]  md:hidden  ">
          {transactionList.count > 0 ? (
            transactionList.items.map((item, index) => (
              <TrasnactionCard key={index} item={item} navigate={navigate} refetch={refetch} />
            ))
          ) : (
            <p>آیتمی وجود ندارد ...</p>
          )}
        </div>

        <div className="w-full rounded-v-lg bg-neutral-00 mx-auto sticky bottom-0 left-0">
          <Pagination
            onPageChange={onPageChange}
            totalCount={transactionList?.count || 0}
            siblingCount={1}
            currentPage={currentPage}
            pageSize={pageSize}
            className=""
          />
        </div>
      </div>
    </div>
  );
};

export default NewTransactionsList;

const TransactionRow = ({ item, navigate, refetch }) => {
  const { mutateAsync: verifyTransaction } = useUpdateTransactionVerified();

  if (!item?.transaction) return null; // Prevent rendering if no transaction

  const { amount, transaction_state, settlement_status, verified_by_user, order_number } = item.transaction;

  const trackingCode = item?.transaction?.vanda_tracking_code;

  const transactionTime =
    item.type === "pos" ? item?.additional_data?.payment_time : item.transaction.invoice_time;

  const handleVerifyTransaction = async () => {
    try {
      const response = await verifyTransaction(trackingCode);
      if (response.status === 200) {
        toast.success("عملیات موفق");
        refetch();
      }
    } catch (error) {
      console.error("Verification failed:", error);
    }
  };

  return (
    <tr className="p-2 bg-neutral-10 gap-2 cursor-pointer hover:bg-neutral-20">
      {/* Terminal & Title */}
      <td className="text-center align-middle">
        <div className="flex justify-center gap-2 min-w-[40px]">
          <ValueFinder data={transactionTerminalOptions} title={item.type} />
        </div>
      </td>
      <td className="text-center align-middle min-w-[200px]" title={item.title ?? "-"}>
        {item.title ?? "-"}
      </td>

      {/* Tracking Code */}
      <td className="text-center align-middle flex-center">
        {trackingCode}
        <CopyButton copyText={trackingCode} />
      </td>

      {/* Amount */}
      <td className="text-center align-middle">{amount?.toLocaleString() ?? "-"}</td>

      {/* Transaction Date & Time */}
      <td className="text-center align-middle min-w-[200px]">
        <div>{unixToPersianDay(transactionTime) ?? "-"}</div>
        <div>{unixToPersianTime(transactionTime) ?? "-"}</div>
      </td>

      {/* Status & Verification */}
      <td>
        <div className="flex items-center gap-2">
          <ValueFinder data={transactionStateOptions} title={transaction_state} />

          {transaction_state === "success" && (
            <>
              <SettlmentTooltipBtn value={settlement_status} />

              <AlertButton
                onClick={handleVerifyTransaction}
                disabled={verified_by_user}
                className="w-12 h-12 hover:bg-primary-70 text-white"
              >
                <VerfiedTooltipBtn value={verified_by_user} />
              </AlertButton>
            </>
          )}
        </div>
      </td>

      {/* Order Number */}
      <td className="flex-center h-full min-w-[110px]">
        <span>{order_number ?? "-"}</span>
        <CopyButton copyText={order_number ?? "-"} />
      </td>

      {/* Card Number */}
      <td className="text-center align-middle min-w-[110px]" dir="ltr">
        {fomatCardNumber(item?.additional_data?.card_number)}
      </td>
      <td>
        <button
          onClick={() => navigate(`/transaction/info/${trackingCode}`)}
          className="flex flex-col w-[60px] justify-center items-center whitespace-nowrap  hover:bg-primary-100 px-1 my-1 text-dark rounded-md disabled:bg-secondary-30 disabled:text-black"
        >
          <img src={eyeIcon} alt="editIcon" className="white-img bg-neutral-20 rounded-full " />
        </button>
      </td>
    </tr>
  );
};
